// src/pages/TransactionDetailPage.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useAuth } from '../context/AuthContext';

import { ModuleRegistry } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { CsvExportModule } from '@ag-grid-community/csv-export';

// Styles
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

// Register modules
ModuleRegistry.registerModules([ClientSideRowModelModule, CsvExportModule]);

// Helper function to generate month dropdown options
const generateMonthOptions = () => {
   const options = [];
   const d = new Date();
   const stop = new Date(2025, 0, 1); // January 2025
   while (d >= stop) {
     const year = d.getFullYear();
     const month = String(d.getMonth() + 1).padStart(2, '0');
     options.push({
       value: `${year}-${month}`,
       label: d.toLocaleString('default', { month: 'long', year: 'numeric' })
     });
     d.setMonth(d.getMonth() - 1);
   }
   return options;
};

const dateOptions = {
  timeZone: 'Asia/Jakarta',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

function formatDateToISO(date) {
  const formattedDate = new Intl.DateTimeFormat('en-GB', dateOptions).format(date);
  const [day, month, year] = formattedDate.split('/');
  return `${year}-${month}-${day}`;
}

// Custom cell renderer for currency
const CurrencyCellRenderer = (params) => {
  if (params.value == null || isNaN(params.value)) return '';
  const formatted = new Intl.NumberFormat('id-ID').format(params.value);
  
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
      <span>Rp</span>
      <span>{formatted}</span>
    </div>
  );
};

const formatNumber = (params) => {
  if (params.value == null || isNaN(params.value)) return '';
  return new Intl.NumberFormat('id-ID').format(params.value);
};

function TransactionDetailPage() {
  const gridRef = useRef(null);
  const auth = useAuth();

  const [rowData, setRowData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [isGridReady, setIsGridReady] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [pgas, setPgas] = useState([]);

  const getToday = new Date();
  const getFirstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const today = formatDateToISO(getToday);
  const firstDayOfMonth = formatDateToISO(getFirstDayOfMonth);

  const [filterType, setFilterType] = useState('daily'); // 'daily' or 'monthly'
  const [selectedMonth, setSelectedMonth] = useState('');
  const monthOptions = generateMonthOptions();
  
  const [filters, setFilters] = useState({
    start_date: firstDayOfMonth,
    end_date: today,
    pga: '',
    agent: '',
    vendor: '',
    transaction_type: 'Transaction',
    value_type: 'amount',
    display_by: 'product_name' // 'product_name' or 'provider_name'
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = async () => {
    if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.showLoadingOverlay();
    }

    let finalFilters = { ...filters };
    
    if (filterType === 'monthly' && selectedMonth) {
      const [year, month] = selectedMonth.split('-');
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0); // Last day of the month

      finalFilters.start_date = formatDateToISO(startDate);
      finalFilters.end_date = formatDateToISO(endDate);
    }

    const params = new URLSearchParams(finalFilters);
    const apiUrl = `/api/transaction-detail/pivot?${params.toString()}`;

    try {
      const response = await auth.authFetch(apiUrl);
      const data = await response.json();

      if (data.columns && data.data) {
        const dynamicColumns = data.columns.map(col => {
          let colDef = {
            field: col.toString(),
            headerName: col.toString().toUpperCase(),
          };

          if (col === 'channel') {
            colDef = { ...colDef, pinned: 'left', filter: true, width: 220 };
          } else if (col === 'Total') {
            colDef = {
              ...colDef,
              width: 180,
              cellRenderer: filters.value_type === 'amount' ? CurrencyCellRenderer : undefined,
              valueFormatter: filters.value_type === 'amount' ? undefined : formatNumber,
              cellStyle: filters.value_type === 'amount' 
                ? { fontWeight: 'bold', padding: '0 12px' }
                : { textAlign: 'right', fontWeight: 'bold' },
              pinned: 'left'
            };
          } else {
            colDef = {
              ...colDef,
              width: 120,
              cellRenderer: filters.value_type === 'amount' ? CurrencyCellRenderer : undefined,
              valueFormatter: filters.value_type === 'amount' ? undefined : formatNumber,
              cellStyle: filters.value_type === 'amount' 
                ? { padding: '0 12px' }
                : { textAlign: 'right' }
            };
          }

          return colDef;
        });

        setColumnDefs(dynamicColumns);
        setRowData(data.data);
      } else {
        setColumnDefs([]);
        setRowData([]);
      }
    } catch (error) {
      console.error("Gagal mengambil data pivot detail:", error);
      setColumnDefs([]);
      setRowData([]);
    } finally {
      if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.hideOverlay();
      }
    }
  };

  // Fetch PGAs and Vendors when component mounts
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const pgaRes = await auth.authFetch('/api/transaction-detail/pgas');
        const pgaData = await pgaRes.json();
        setPgas(pgaData);

        const vendorRes = await auth.authFetch('/api/transaction-vendors');
        const vendorData = await vendorRes.json();
        setVendors(vendorData);
      } catch (error) {
        console.error("Gagal mengambil data filter dropdown:", error);
      }
    };

    fetchDropdowns();
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onExportClick = () => {
    const api = gridRef.current?.api;
    if (!api) {
      console.error("Grid API tidak tersedia saat mencoba ekspor.");
      return;
    }
    api.exportDataAsCsv({
      fileName: `Detail_${filters.transaction_type}_${filters.value_type}_${filters.start_date}_${filters.end_date}.csv`,
      suppressQuotes: true,
      processCellCallback: (params) => {
        const rawValue = params.value;
        if (params.column.getColId() === 'channel' || params.column.getColId() === 'Total') {
          return rawValue;
        }

        // Hilangkan format 'Rp' dan separator ribuan untuk nilai numerik
        if (typeof rawValue === 'string') {
          const cleaned = rawValue.replace(/Rp/g, '').replace(/\./g, '').replace(/,/g, '.').trim();
          return isNaN(cleaned) ? rawValue : cleaned;
        }

        return rawValue;
      }
    });
  };

  const onGridReady = useCallback(() => {
    setIsGridReady(true);
  }, []);

  const onGridPreDestroyed = useCallback(() => {
    setIsGridReady(false);
  }, []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
  }), []);

  const getRowStyle = params => {
    if (params.data?.channel === 'Total') {
      return { fontWeight: 'bold', backgroundColor: '#343a40', color: 'white' };
    }
  };

  const getRowId = params => {
    if (params.data?.channel === 'Total') return 'TotalRow';
    return params.data?.channel ?? '';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <h1>Transaction Detail</h1>

      <div className="pivot-filters" style={{ gap: 8, display: 'flex', flexWrap: 'wrap', marginBottom: 8, alignItems: 'center' }}>
        {/* Filter Type Selector */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <label>
            <input 
              type="radio" 
              value="daily" 
              checked={filterType === 'daily'} 
              onChange={(e) => setFilterType(e.target.value)}
            />
            {' '}Harian
          </label>
          <label>
            <input 
              type="radio" 
              value="monthly" 
              checked={filterType === 'monthly'} 
              onChange={(e) => setFilterType(e.target.value)}
            />
            {' '}Bulanan
          </label>
        </div>
        
        {/* Daily Filter */}
        {filterType === 'daily' && (
          <>
            <input type="date" name="start_date" value={filters.start_date} onChange={handleFilterChange} />
            <input type="date" name="end_date" value={filters.end_date} onChange={handleFilterChange} />
          </>
        )}
        
        {/* Monthly Filter */}
        {filterType === 'monthly' && (
          <select 
            value={selectedMonth} 
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value="">Pilih Bulan</option>
            {monthOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )}
        
        {/* Dropdown PGA */}
        <select name="pga" value={filters.pga} onChange={handleFilterChange}>
          <option value="">All PGA</option>
          {pgas.map(p => (
            <option key={p.id} value={p.id}>{p.pga_name}</option>
          ))}
        </select>

        {/* Dropdown Agent */}
        <select name="agent" value={filters.agent} onChange={handleFilterChange}>
          <option value="">All Agents</option>
          <option value="B2B">B2B</option>
          <option value="B2C">B2C</option>
        </select>

        {/* Dropdown Vendor */}
        <select name="vendor" value={filters.vendor} onChange={handleFilterChange}>
          <option value="">All Vendors</option>
          {vendors.map(vendor => (
            <option key={vendor} value={vendor}>{vendor}</option>
          ))}
        </select>

        {/* Dropdown Tipe Transaksi */}
        <select name="transaction_type" value={filters.transaction_type} onChange={handleFilterChange}>
          <option value="Transaction">Transaction</option>
          <option value="Disbursement">Disbursement</option>
        </select>

        {/* Dropdown Tipe Nilai */}
        <select name="value_type" value={filters.value_type} onChange={handleFilterChange}>
          <option value="amount">Amount</option>
          <option value="entries">Entries</option>
        </select>

        {/* Dropdown Mapped Issuer display format */}
        <select name="display_by" value={filters.display_by} onChange={handleFilterChange}>
          <option value="product_name">Nama Produk</option>
          <option value="provider_name">Nama Penyelenggara</option>
        </select>

        <button onClick={applyFilters}>Apply Filter</button>
        <button onClick={onExportClick} disabled={!isGridReady || rowData.length === 0}>Export to CSV</button>
      </div>

      <div className="ag-theme-alpine" style={{ flex: 1, width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowStyle={getRowStyle}
          getRowId={getRowId}
          onGridReady={onGridReady}
          onGridPreDestroyed={onGridPreDestroyed}
          overlayNoRowsTemplate="<span>No data found for the selected filters.</span>"
          overlayLoadingTemplate="<span>Loading...</span>"
        />
      </div>
    </div>
  );
}

export default TransactionDetailPage;
